import { memo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { FolderOpen } from 'lucide-react'

interface JiraProjectBreakdownProps {
  tasksByProject: Record<string, number>
  title?: string
}

const projectColors = [
  'bg-primary-500',
  'bg-purple-500',
  'bg-blue-500',
  'bg-green-500',
  'bg-amber-500',
  'bg-red-500',
  'bg-indigo-500',
  'bg-pink-500',
]

const JiraProjectBreakdown = memo(function JiraProjectBreakdown({ 
  tasksByProject,
  title = 'Projects'
}: JiraProjectBreakdownProps) {
  const sortedProjects = Object.entries(tasksByProject).sort((a, b) => b[1] - a[1])
  const total = sortedProjects.reduce((sum, [, count]) => sum + count, 0)

  return (
    <Card variant="compact" padding="sm">
      <CardHeader compact>
        <CardTitle compact className="flex items-center gap-2 text-sm">
          <FolderOpen className="h-4 w-4 text-primary-600" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent compact>
        <div className="space-y-3">
          {sortedProjects.slice(0, 6).map(([project, count], index) => {
            const percentage = total > 0 ? (count / total) * 100 : 0
            const color = projectColors[index % projectColors.length]

            return (
              <div key={project}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{project}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {count} tasks
                  </span>
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${color} rounded-full transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
        {sortedProjects.length > 6 && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
            +{sortedProjects.length - 6} more projects
          </p>
        )}
      </CardContent>
    </Card>
  )
})

export default JiraProjectBreakdown

